import { z } from 'zod';
import { MOTION_PRESET_IDS, type MotionPresetId } from '../animation/motionPresets';
import { PathSchema, TemplateIdSchema } from '../animation/procedural/spec';
import {
  ANIMATION_FPS,
  BlendModeSchema,
  CHARACTER_STYLES,
  EFFECT_RADIUS_MAX,
  ChannelSchema,
  EasingSchema,
  EventCategorySchema,
  HexColorSchema,
  IdSchema,
  LoopModeSchema,
  MAX_ANIMATION_SECONDS,
  MAX_GRADIENT_STOPS,
  MAX_TEXT_LENGTH,
  ReactionTypeSchema,
} from '../domain/schemas';
import { CHARACTER_PRESET_IDS, type CharacterPresetId } from '../scene/characters';
import { SCENE_PRESET_IDS, type ScenePresetId } from '../scene/presets';
import { VISUALIZER_PRESETS } from '../visualizer/presets';

/**
 * Assistant creation packages (Phase 5A): a versioned, declarative list of creative
 * changes written by an external assistant (ChatGPT, Claude) and applied locally by
 * SonicForge after the owner previews and confirms them.
 *
 * A package is untrusted input. Every object is strict (unknown fields are rejected),
 * every value is bounded, and nothing in it is executed: it names existing operations and
 * data only. There are no URLs, file paths, HTML, SVG or scripts; assets are referenced by
 * the id they already have in the project.
 */

export const PACKAGE_FORMAT = 'sonicforge-creation-package';
/**
 * Version 2 (Phase 5B.1) adds the Look v1 fields: shape gradients, object glow and shadow,
 * layer opacity and blend, and the scene grade and vignette. Version 1 packages are still
 * accepted unchanged; using a version 2 field in a version 1 package is refused with a
 * message naming the field (see `VERSION_2_FIELDS`).
 */
export const PACKAGE_VERSION = 2;
export const SUPPORTED_PACKAGE_VERSIONS = [1, 2] as const;

export const PACKAGE_LIMITS = {
  /** Size of the pasted or uploaded JSON text. */
  bytes: 512 * 1024,
  operations: 400,
  keysPerOperation: 600,
  keysTotal: 10_000,
  objectsPerOperation: 200,
  explanation: 4000,
  motionSteps: 32,
} as const;

/** A name the package gives to something it creates: lowercase, digits and dashes. */
export const RefNameSchema = z.string().regex(/^[a-z][a-z0-9-]{0,39}$/, 'Use lowercase letters, digits and dashes (e.g. "warrior")');
/** "@name" for something this package created earlier, or the id of something that already exists. */
export const RefSchema = z.union([
  z.string().regex(/^@[a-z][a-z0-9-]{0,39}$/, 'A reference to a package item starts with @'),
  IdSchema,
]);
export type Ref = z.infer<typeof RefSchema>;

const Name = z.string().trim().min(1).max(60);
const Coord = z.number().min(-100_000).max(100_000);
const Size = z.number().min(1).max(100_000);
const Unit = z.number().min(0).max(1);
const Seconds = z.number().min(0).max(MAX_ANIMATION_SECONDS);

const enumOf = <T extends string>(values: readonly T[]) => z.enum(values as [T, ...T[]]);
const ScenePresetSchema = enumOf<ScenePresetId>(SCENE_PRESET_IDS);
const CharacterPresetSchema = enumOf<CharacterPresetId>(CHARACTER_PRESET_IDS);
const MotionPresetSchema = enumOf<MotionPresetId>(MOTION_PRESET_IDS);
const VisualizerPresetSchema = enumOf(VISUALIZER_PRESETS.map((p) => p.id));

const GradientSpec = z.strictObject({ to: HexColorSchema, angle: z.number().min(0).max(360) });

// ------------------------------------------------------------------ look (version 2)

const Stops = z
  .array(z.strictObject({ offset: Unit, color: HexColorSchema, opacity: Unit.optional() }))
  .min(2)
  .max(MAX_GRADIENT_STOPS)
  .refine((stops) => stops.every((s, i) => i === 0 || s.offset >= stops[i - 1]!.offset), 'Gradient stops must be in increasing order of offset');

/** A shape fill gradient in the shape's own box. Replaces any previous gradient; null removes it. */
export const FillGradientSpecSchema = z.discriminatedUnion('type', [
  /** angle: 0 = left → right, 90 = top → bottom (default 90). */
  z.strictObject({ type: z.literal('linear'), stops: Stops, angle: z.number().min(0).max(360).optional() }),
  /** Centre and radius as fractions of the box (defaults 0.5, 0.5, 0.5). */
  z.strictObject({ type: z.literal('radial'), stops: Stops, cx: Unit.optional(), cy: Unit.optional(), radius: z.number().min(0.05).max(1.5).optional() }),
]);
export type FillGradientSpec = z.infer<typeof FillGradientSpecSchema>;

/** Fields left out keep their current value, or the default when the effect is new. */
export const GlowSpecSchema = z.strictObject({ color: HexColorSchema, radius: z.number().min(1).max(EFFECT_RADIUS_MAX), strength: Unit }).partial();
export const ShadowSpecSchema = z
  .strictObject({ color: HexColorSchema, blur: z.number().min(0).max(EFFECT_RADIUS_MAX), opacity: Unit, offsetX: z.number().min(-200).max(200), offsetY: z.number().min(-200).max(200) })
  .partial();
export const GradeSpecSchema = z
  .strictObject({ brightness: z.number().min(0).max(2), contrast: z.number().min(0).max(2), saturation: z.number().min(0).max(2), tint: HexColorSchema, tintAmount: Unit })
  .partial();
export const VignetteSpecSchema = z.strictObject({ amount: Unit, softness: Unit, color: HexColorSchema }).partial();
export type GlowSpec = z.infer<typeof GlowSpecSchema>;
export type ShadowSpec = z.infer<typeof ShadowSpecSchema>;
export type GradeSpec = z.infer<typeof GradeSpecSchema>;
export type VignetteSpec = z.infer<typeof VignetteSpecSchema>;

/** Scene finishing. Each part merges with the scene's current value; null removes it. */
export const LookSpecSchema = z.strictObject({ grade: GradeSpecSchema.nullable().optional(), vignette: VignetteSpecSchema.nullable().optional() });
export type LookSpec = z.infer<typeof LookSpecSchema>;

const LayerLook = { opacity: Unit.optional(), blend: BlendModeSchema.optional() };

// ------------------------------------------------------------------ objects

const Transform = {
  name: Name.optional(),
  /** Top-left of the object's box, in scene pixels (origin top-left, y down). */
  x: Coord,
  y: Coord,
  width: Size,
  height: Size,
  /** Degrees, clockwise. */
  rotation: z.number().min(-360).max(360).optional(),
  opacity: Unit.optional(),
  visible: z.boolean().optional(),
  locked: z.boolean().optional(),
  anchorX: Unit.optional(),
  anchorY: Unit.optional(),
  glow: GlowSpecSchema.nullable().optional(),
  shadow: ShadowSpecSchema.nullable().optional(),
};
const Stroke = z.strictObject({ color: HexColorSchema, width: z.number().min(0).max(64) }).nullable();
const Shape = { fill: HexColorSchema.optional(), stroke: Stroke.optional(), gradient: FillGradientSpecSchema.nullable().optional() };

export const ObjectSpecSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('rect'), ...Transform, ...Shape, cornerRadius: z.number().min(0).max(1024).optional() }),
  z.strictObject({ type: z.literal('circle'), ...Transform, ...Shape }),
  z.strictObject({ type: z.literal('ellipse'), ...Transform, ...Shape }),
  z.strictObject({ type: z.literal('polygon'), ...Transform, ...Shape, points: z.array(z.tuple([Unit, Unit])).min(3).max(64).optional() }),
  z.strictObject({
    type: z.literal('text'),
    ...Transform,
    text: z.string().min(1).max(MAX_TEXT_LENGTH),
    fontSize: z.number().min(6).max(512).optional(),
    fontFamily: z.enum(['sans', 'serif', 'mono']).optional(),
    bold: z.boolean().optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    color: HexColorSchema.optional(),
  }),
  z.strictObject({ type: z.literal('image'), ...Transform, asset: IdSchema, fit: z.enum(['stretch', 'contain', 'cover']).optional() }),
  z.strictObject({ type: z.literal('character'), ...Transform, character: RefSchema, flipX: z.boolean().optional() }),
]);
export type ObjectSpec = z.infer<typeof ObjectSpecSchema>;

/** Fields `object.update` may set; type-specific ones are checked against the object's type. */
export const ObjectPatchSchema = z
  .strictObject({
    name: Name,
    x: Coord,
    y: Coord,
    width: Size,
    height: Size,
    rotation: z.number().min(-360).max(360),
    opacity: Unit,
    visible: z.boolean(),
    locked: z.boolean(),
    anchorX: Unit,
    anchorY: Unit,
    layer: RefSchema,
    glow: GlowSpecSchema.nullable(),
    shadow: ShadowSpecSchema.nullable(),
    fill: HexColorSchema,
    stroke: Stroke,
    gradient: FillGradientSpecSchema.nullable(),
    cornerRadius: z.number().min(0).max(1024),
    points: z.array(z.tuple([Unit, Unit])).min(3).max(64),
    text: z.string().min(1).max(MAX_TEXT_LENGTH),
    fontSize: z.number().min(6).max(512),
    fontFamily: z.enum(['sans', 'serif', 'mono']),
    bold: z.boolean(),
    align: z.enum(['left', 'center', 'right']),
    color: HexColorSchema,
    fit: z.enum(['stretch', 'contain', 'cover']),
    flipX: z.boolean(),
  })
  .partial()
  .refine((p) => Object.keys(p).length > 0, 'Set at least one field');
export type ObjectPatchSpec = z.infer<typeof ObjectPatchSchema>;

// ------------------------------------------------------------------ characters

const Proportions = z
  .strictObject({
    headSize: z.number().min(0.12).max(0.32),
    torsoWidth: z.number().min(0.3).max(0.7),
    torsoHeight: z.number().min(0.2).max(0.4),
    legLength: z.number().min(0.2).max(0.45),
    armLength: z.number().min(0.6).max(1.3),
    limbThickness: z.number().min(0.06).max(0.16),
  })
  .partial();

export const AppearanceSchema = z.strictObject({
  skin: HexColorSchema.optional(),
  head: z.strictObject({ shape: z.enum(CHARACTER_STYLES.head) }).optional(),
  eyes: z.strictObject({ style: z.enum(CHARACTER_STYLES.eyes), color: HexColorSchema }).partial().optional(),
  hair: z.strictObject({ style: z.enum(CHARACTER_STYLES.hair), color: HexColorSchema }).partial().optional(),
  clothing: z
    .strictObject({
      top: z.enum(CHARACTER_STYLES.top),
      topColor: HexColorSchema,
      accentColor: HexColorSchema,
      bottom: z.enum(CHARACTER_STYLES.bottom),
      bottomColor: HexColorSchema,
      shoeColor: HexColorSchema,
    })
    .partial()
    .optional(),
  /** Replaces the character's accessories (at most 4). */
  accessories: z.array(z.strictObject({ kind: z.enum(CHARACTER_STYLES.accessory), color: HexColorSchema })).max(4).optional(),
});
export type AppearanceSpec = z.infer<typeof AppearanceSchema>;

// ------------------------------------------------------------------ motion (Phase 3C templates)

const MotionStepSpec = z.strictObject({
  id: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/),
  template: TemplateIdSchema,
  /** Slot names (see `slots`). */
  targets: z.array(RefNameSchema).min(1).max(8),
  timing: z
    .strictObject({
      mode: z.enum(['sequence', 'with', 'after', 'at']),
      ref: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/).nullable().optional(),
      at: Seconds.nullable().optional(),
      delay: Seconds.optional(),
    })
    .optional(),
  duration: z.number().positive().max(MAX_ANIMATION_SECONDS).nullable().optional(),
  repeat: z.number().int().min(1).max(64).optional(),
  stagger: z.number().min(0).max(10).optional(),
  easing: EasingSchema.nullable().optional(),
  params: z.record(z.string().max(32), z.union([z.number(), z.string().max(40), z.boolean(), z.null()])).optional(),
  path: PathSchema.nullable().optional(),
  variation: z.number().min(0).max(1).optional(),
  seed: z.number().int().min(0).max(2 ** 31 - 1).nullable().optional(),
});

// ------------------------------------------------------------------ operations

const op = <T extends string, S extends z.ZodRawShape>(name: T, shape: S) => z.strictObject({ op: z.literal(name), ...shape });

export const OperationSchema = z.discriminatedUnion('op', [
  op('scene.create', {
    ref: RefNameSchema,
    name: Name,
    /** Start from a built-in environment (neon-city, dark-studio, forest). */
    preset: ScenePresetSchema.optional(),
    width: z.number().int().min(64).max(4096).optional(),
    height: z.number().int().min(64).max(4096).optional(),
    background: HexColorSchema.optional(),
    gradient: GradientSpec.nullable().optional(),
    look: LookSpecSchema.optional(),
    /** Make it the scene shown in the editor. */
    activate: z.boolean().optional(),
  }),
  op('scene.update', {
    scene: RefSchema,
    name: Name.optional(),
    width: z.number().int().min(64).max(4096).optional(),
    height: z.number().int().min(64).max(4096).optional(),
    background: HexColorSchema.optional(),
    gradient: GradientSpec.nullable().optional(),
    look: LookSpecSchema.optional(),
    activate: z.boolean().optional(),
  }),
  op('scene.delete', { scene: RefSchema }),
  op('layer.create', { ref: RefNameSchema, scene: RefSchema, name: Name, ...LayerLook }),
  op('layer.update', { scene: RefSchema, layer: RefSchema, name: Name.optional(), visible: z.boolean().optional(), locked: z.boolean().optional(), ...LayerLook }),
  op('character.create', { ref: RefNameSchema, preset: CharacterPresetSchema, name: Name.optional(), appearance: AppearanceSchema.optional(), proportions: Proportions.optional() }),
  op('character.update', { character: RefSchema, name: Name.optional(), appearance: AppearanceSchema.optional(), proportions: Proportions.optional() }),
  op('object.create', { ref: RefNameSchema, scene: RefSchema, layer: RefSchema.optional(), object: ObjectSpecSchema }),
  op('object.update', { scene: RefSchema, object: RefSchema, set: ObjectPatchSchema }),
  op('object.delete', { scene: RefSchema, objects: z.array(RefSchema).min(1).max(PACKAGE_LIMITS.objectsPerOperation) }),
  op('object.order', { scene: RefSchema, objects: z.array(RefSchema).min(1).max(PACKAGE_LIMITS.objectsPerOperation), to: z.enum(['front', 'back', 'forward', 'backward']) }),
  op('animation.create', {
    ref: RefNameSchema,
    scene: RefSchema,
    name: Name.optional(),
    seconds: z.number().positive().max(MAX_ANIMATION_SECONDS),
    fps: z.union(ANIMATION_FPS.map((f) => z.literal(f)) as [z.ZodLiteral<12>, z.ZodLiteral<24>, z.ZodLiteral<30>, z.ZodLiteral<60>]).optional(),
    loop: LoopModeSchema.optional(),
  }),
  op('animation.keyframes', {
    animation: RefSchema,
    object: RefSchema,
    channel: ChannelSchema,
    /** Times in seconds from the start of the animation; rounded to the nearest frame. */
    keys: z
      .array(z.strictObject({ time: Seconds, value: z.number().min(-100_000).max(100_000), easing: EasingSchema.optional() }))
      .min(1)
      .max(PACKAGE_LIMITS.keysPerOperation),
  }),
  op('animation.preset', { animation: RefSchema, object: RefSchema, preset: MotionPresetSchema }),
  op('animation.motion', {
    animation: RefSchema,
    /** Names the plan's targets: each slot is bound to an object in the animation's scene. */
    slots: z.array(z.strictObject({ name: RefNameSchema, object: RefSchema })).min(1).max(8),
    steps: z.array(MotionStepSpec).min(1).max(PACKAGE_LIMITS.motionSteps),
    seed: z.number().int().min(0).max(2 ** 31 - 1).optional(),
    totalSeconds: z.number().positive().max(MAX_ANIMATION_SECONDS).nullable().optional(),
    /** How generated keys meet existing ones (default: merge). */
    policy: z.enum(['merge', 'replace-range', 'append']).optional(),
    /** Allow the motion to lengthen the animation. */
    allowExtend: z.boolean().optional(),
  }),
  op('sync.session', { ref: RefNameSchema, scene: RefSchema, animation: RefSchema.nullable().optional(), name: Name.optional() }),
  op('sync.reaction', {
    session: RefSchema,
    reaction: ReactionTypeSchema,
    categories: z.array(EventCategorySchema).min(1).max(8),
    /** The object that reacts; omit for scene reactions (flash, background, shake). */
    object: RefSchema.nullable().optional(),
    name: Name.optional(),
    every: z.number().int().min(1).max(256).optional(),
    offsetBeats: z.number().min(-16).max(16).optional(),
  }),
  op('visualizer.preset', { session: RefSchema, preset: VisualizerPresetSchema }),
]);
export type Operation = z.infer<typeof OperationSchema>;
export type OperationName = Operation['op'];
export const OPERATION_NAMES = OperationSchema.options.map((o) => o.shape.op.value) as OperationName[];

/** Operations that remove or replace existing content: they need explicit confirmation. */
export const DESTRUCTIVE_OPERATIONS: readonly OperationName[] = ['scene.delete', 'object.delete'];

export const PackageSchema = z.strictObject({
  format: z.literal(PACKAGE_FORMAT),
  version: z.union([z.literal(1), z.literal(2)]),
  /** Stable package id (lowercase, digits, dashes): new ids are derived from it. */
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{2,63}$/, 'Use 3–64 lowercase letters, digits and dashes'),
  title: z.string().trim().min(1).max(80),
  author: z.string().trim().min(1).max(60).optional(),
  /** Shown to the owner before applying. Plain text. */
  explanation: z.string().max(PACKAGE_LIMITS.explanation).optional(),
  target: z
    .strictObject({
      /** Only apply to this project (from the Project Context export). */
      projectId: IdSchema.optional(),
      /** Only apply if the project is unchanged since the context was exported. */
      contextRevision: z.string().regex(/^r[0-9a-f]{16}$/).optional(),
      /** The project schema version the package was written against. */
      schemaVersion: z.number().int().min(1).max(1000).optional(),
    })
    .optional(),
  operations: z.array(OperationSchema).min(1).max(PACKAGE_LIMITS.operations),
});
export type CreationPackage = z.infer<typeof PackageSchema>;

/** Fields that need package version 2, per operation (`object` / `set` are nested specs). */
export const VERSION_2_FIELDS = {
  'scene.create': ['look'],
  'scene.update': ['look'],
  'layer.create': ['opacity', 'blend'],
  'layer.update': ['opacity', 'blend'],
  'object.create': ['object.gradient', 'object.glow', 'object.shadow'],
  'object.update': ['set.gradient', 'set.glow', 'set.shadow'],
} as const satisfies Partial<Record<OperationName, readonly string[]>>;

/** Version 2 fields used by a package, as `operations[i].path` (empty for version 2 packages). */
export function version2FieldsIn(pkg: CreationPackage): string[] {
  if (pkg.version >= 2) return [];
  const found: string[] = [];
  pkg.operations.forEach((o, i) => {
    for (const field of (VERSION_2_FIELDS as Partial<Record<OperationName, readonly string[]>>)[o.op] ?? []) {
      const value = field.split('.').reduce<unknown>((v, k) => (v && typeof v === 'object' ? (v as Record<string, unknown>)[k] : undefined), o);
      if (value !== undefined) found.push(`operations[${i}].${field}`);
    }
  });
  return found;
}
